import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import utilities from '@/shared/utilities.service';
import { IBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import {
    B2CClientService,
    PersonalOfferOverviewModel,
    PersonalOfferViewModel,
} from '@/b2c/shared/api/server-apis';
import { QueryKeys } from '@/shared/config/route-query.config';
import { RouteLocationRaw } from 'vue-router';
import { TempBasketViewModel } from '@/shared/checkout/basket/temp-basket-api';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';
import useTEMPB2CFeatureBasket from '@/b2c/basket/useTEMPB2CFeatureBasket';

const DISCOUNT_COUNTDOWN_NUMBER_OF_DAYS = 14;

const { addCustomerOfferItem } = useTEMPB2CFeatureBasket();

class OffersStore {
    private state: IOffersState = reactive({
        detail: {} as PersonalOfferViewModel,
        offers: [] as PersonalOfferOverviewModel[],
        pending: false,
    });

    constructor() {
        makeStoreDebugAccessible('offersStore', this.state);
    }

    async updateOffers(): Promise<PersonalOfferOverviewModel[]> {
        this.state.pending = true;
        const offers = await B2CClientService.getPersonalOffers().then(utilities.unwrapModel);
        this.state.pending = false;
        return this.state.offers = offers || [] as PersonalOfferOverviewModel[];
    }

    async updateDetails(id: string): Promise<PersonalOfferViewModel> {
        const detail = await B2CClientService.getPersonalOfferById(id).then(utilities.unwrapModel);
        return this.state.detail = detail ?? {} as PersonalOfferViewModel;
    }

    async isValidOfferId(id: string, messageId?: string): Promise<boolean> {
        try {
            const offer = await B2CClientService.getPersonalOfferById(id, undefined, messageId).then(utilities.unwrapModel);
            return !!offer?.offerId ?? false;
        } catch (e) {
            return false;
        }
    }

    async addToBasket(): Promise<TempBasketViewModel> {
        basketTrackingService.customerOfferAll(this.detail);
        return addCustomerOfferItem(this.detail.offerId, []);
    }

    async addItemToBasket(lineItemId: string): Promise<TempBasketViewModel> {
        basketTrackingService.customerOfferItem(this.detail, lineItemId);
        return addCustomerOfferItem(this.detail.offerId, [lineItemId]);
    }

    async clearDetail() {
        this.state.detail = {} as PersonalOfferViewModel;
    }

    get offers(): PersonalOfferOverviewModel[] {
        return this.state.offers;
    }

    get detail(): PersonalOfferViewModel {
        return this.state.detail;
    }

    get pending(): boolean {
        return this.state.pending;
    }

    getEmptyResultText(labels: IBlockLabels): string {
        return this.state.pending ? labels.loading : labels.noItemsAdded;
    }

    getDetailsLocation(id: string, detailsUrl: string, addToBasket: boolean = false): RouteLocationRaw {
        const query = {
            [QueryKeys.Id]: id.toString(),
        };

        if (addToBasket) {
            query[QueryKeys.AddToBasket] = 'true';
        }

        return {
            path: detailsUrl,
            query,
        };
    }

    getShowDiscountCountdown(days?: number) {
        const expirationDate = days ?? -1;
        return expirationDate > -1 && expirationDate <= DISCOUNT_COUNTDOWN_NUMBER_OF_DAYS;
    }
}

export interface IOffersState {
    offers: PersonalOfferOverviewModel[];
    detail: PersonalOfferViewModel;
    pending: boolean;
}

export default new OffersStore();
